import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { articlesAPI, categoriesAPI, adminAPI } from '../../lib/api';
import { UnifiedContentEditor, SidebarCard } from '../../components/admin/UnifiedContentEditor';
import { CitationPicker } from '../../components/admin/CitationPicker';
import { ReferencesManager } from '../../components/admin/ReferencesManager';

const DEFAULT_CATEGORIES = ['General', 'History', 'Culture', 'Architecture', 'People', 'Events'];

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const editorRef = useRef(null);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  
  // Core content
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  
  // Metadata
  const [category, setCategory] = useState('General');
  const [visibility, setVisibility] = useState('public');
  const [published, setPublished] = useState(true);
  
  // Citations and tags
  const [citations, setCitations] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  
  // Citation picker state
  const [showCitationPicker, setShowCitationPicker] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const catResponse = await categoriesAPI.getAll();
        if (catResponse.data.length > 0) {
          setCategories(catResponse.data);
        }
        
        if (isEditing) {
          // Try to get article - use admin preview endpoint which works for unpublished articles
          let data;
          try {
            // First try the regular endpoint (for published articles)
            const response = await articlesAPI.getOne(id);
            data = response.data;
          } catch (err) {
            // If not found (unpublished), use admin preview endpoint
            const response = await adminAPI.previewArticle(id);
            data = response.data;
          }
          setTitle(data.title || '');
          setExcerpt(data.excerpt || '');
          setContent(data.content || '');
          setFeaturedImage(data.featured_image || '');
          setCategory(data.category || 'General');
          setVisibility(data.visibility || 'public');
          setCitations(data.citations || []);
          setTags(data.tags || []);
          setPublished(data.published !== false);
        }
      } catch (error) {
        toast.error('Failed to load data');
        navigate('/admin/articles');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditing, navigate]);

  const addTag = () => {
    if (!newTag.trim()) return;
    
    // Split by comma and process each tag
    const newTags = newTag
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag && !tags.includes(tag)); // Filter empty and duplicates
    
    if (newTags.length > 0) {
      setTags([...tags, ...newTags]);
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  // Handle selecting a citation from the picker
  const handleSelectCitation = (citationIndex) => {
    if (editorRef.current) {
      editorRef.current.insertCitation(citationIndex);
    }
  };

  // Handle adding a new citation
  const handleAddCitation = (citationHtml) => {
    setCitations([...citations, citationHtml]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !excerpt.trim()) {
      toast.error('Please fill in title and excerpt');
      return;
    }

    setSaving(true);

    const formData = {
      title,
      excerpt,
      content,
      featured_image: featuredImage,
      category,
      visibility,
      citations,
      tags,
      published
    };

    try {
      if (isEditing) {
        await articlesAPI.update(id, formData);
        toast.success('Article updated successfully');
      } else {
        await articlesAPI.create(formData);
        toast.success('Article created successfully');
      }
      navigate('/admin/articles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  // Sidebar content for articles
  const sidebarContent = (
    <>
      {/* Publishing Options */}
      <SidebarCard title="Publishing">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="published" className="text-sm">Published</Label>
            <Switch
              id="published"
              checked={published}
              onCheckedChange={setPublished}
              data-testid="article-published"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Visibility</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="article-visibility" className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (Full access)</SelectItem>
                <SelectItem value="restricted">Restricted (Login required)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Restricted articles show only the excerpt to non-logged users.
            </p>
          </div>
        </div>
      </SidebarCard>

      {/* Category */}
      <SidebarCard title="Category">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="article-category" className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SidebarCard>

      {/* References Manager - Inline Citations */}
      <SidebarCard title="">
        <ReferencesManager
          citations={citations}
          onCitationsChange={setCitations}
          maxHeight="200px"
        />
      </SidebarCard>

      {/* Tags */}
      <SidebarCard title="Tags">
        <div className="flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="tag1, tag2, tag3"
            className="text-sm"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            data-testid="new-tag"
          />
          <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Separate multiple tags with commas</p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {tags.map((tag) => (
              <span key={tag} className="tag-badge flex items-center gap-1 text-xs">
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </SidebarCard>
    </>
  );

  return (
    <>
      <UnifiedContentEditor
        ref={editorRef}
        contentType="article"
        pageTitle={isEditing ? 'Edit Article' : 'New Article'}
        
        // Core content
        title={title}
        onTitleChange={setTitle}
        titlePlaceholder="Enter article title..."
        
        // Excerpt
        showExcerpt={true}
        excerpt={excerpt}
        onExcerptChange={setExcerpt}
        excerptPlaceholder="Brief introduction (shown to all users, even when content is restricted)..."
        excerptHelp="This excerpt is visible to all users, even when the full content is restricted."
        
        // Content
        content={content}
        onContentChange={setContent}
        contentPlaceholder="Start writing your heritage story here..."
        
        // Featured image
        featuredImage={featuredImage}
        onFeaturedImageChange={setFeaturedImage}
        
        // Actions
        onBack={() => navigate('/admin/articles')}
        onSubmit={handleSubmit}
        saving={saving}
        isEditing={isEditing}
        submitLabel={isEditing ? 'Update Article' : 'Publish Article'}
        
        // Sidebar
        sidebarContent={sidebarContent}
        
        // Citation support
        citations={citations}
        onInsertCitation={() => setShowCitationPicker(true)}
        showCitationButton={true}
      />

      {/* Citation Picker Modal */}
      <CitationPicker
        open={showCitationPicker}
        onOpenChange={setShowCitationPicker}
        citations={citations}
        onSelectCitation={handleSelectCitation}
        onAddCitation={handleAddCitation}
      />
    </>
  );
}
